import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Zap, RefreshCw, BarChart3, AlertTriangle, Droplets, Settings } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
const InverterEfficiencyChart = ({
  title = "Inverter Efficiency Metrics"
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const {
    toast
  } = useToast();
  useEffect(() => {
    fetchInverterData();
  }, [startDate, endDate]);
  const fetchInverterData = async () => {
    try {
      setIsLoading(true);
      const startDateTime = startDate + ' 00:00:00';
      const endDateTime = endDate + ' 23:55:00';
      console.log('Inverter Efficiency: Fetching data with params:', {
        startDateTime,
        endDateTime,
        plant_id: API_CONFIG.PLANT_ID
      });

      // Fetch energy loss data which contains inverter metrics
      const response = await apiService.getForEnergyLoss({
        start_date: startDateTime,
        end_date: endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      console.log('Inverter Efficiency: API Response:', response);
      const arr = Array.isArray(response.data) ? response.data : (response.data?.body || []);
      let energyLossData = null;
      if (arr.length > 0 && arr[0].readings && arr[0].readings.length > 0) {
        energyLossData = arr[0].readings[0].EYA;
      } else if (response.data && response.data.EYA) {
        energyLossData = response.data.EYA;
      }
      if (energyLossData) {
        console.log('Inverter Efficiency: Energy Loss Data:', energyLossData);
        const processedData = [{
          date: new Date().toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }),
          inverterEfficiency: Number((energyLossData.inverter_efficiency_loss ?? energyLossData.inverter_efficiency_loss_percentage ?? 0).toFixed(2)),
          inverterBreakdown: Number((energyLossData.plant_breakdown_loss_kwh ?? energyLossData.inverter_breakdown_kwh ?? 0).toFixed(2)),
          inverterClipping: Number((energyLossData.plant_clipping_loss_percentage ?? energyLossData.inverter_clipping_loss_percentage ?? 0).toFixed(2)),
          soilingLoss: Number(Math.abs(energyLossData.soiling_loss ?? energyLossData.soiling_loss_percentage ?? 0).toFixed(2))
        }];
        console.log('Inverter Efficiency: Processed Data:', processedData);
        setData(processedData);
      }
    } catch (error) {
      console.error('Error fetching inverter data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch inverter efficiency data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </p>
          {payload.map((entry, index) => <p key={index} className="text-sm" style={{
          color: entry.color
        }}>
              {entry.name}: {formatNumber(entry.value)}
              {entry.name === 'Inverter Efficiency' || entry.name === 'Inverter Clipping' || entry.name === 'Soiling Loss' ? '%' : ' kWh'}
            </p>)}
        </div>;
    }
    return null;
  };
  const CustomLegend = ({
    payload
  }) => {
    return <div className="flex justify-center space-x-6 mt-4">
        {payload?.map((entry, index) => <div key={index} className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color
        }}></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {entry.value}
            </span>
          </div>)}
      </div>;
  };
  return <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-blue-500" />
            <span>{title}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={fetchInverterData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Date Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full" />
          </div>
        </div>

        {/* Chart */}
        {isLoading ? <div className="space-y-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </div> : data.length > 0 ? <div className="space-y-4">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data} margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 80
          }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{
              fontSize: 12
            }} />
                <YAxis tick={{
              fontSize: 12
            }} label={{
              value: 'Value (%) / kWh',
              angle: -90,
              position: 'insideLeft'
            }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="inverterEfficiency" fill="#3B82F6" name="Inverter Efficiency (%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="inverterClipping" fill="#EF4444" name="Inverter Clipping (%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="soilingLoss" fill="#F59E0B" name="Soiling Loss (%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="inverterBreakdown" fill="#8B5CF6" name="Inverter Breakdown (kWh)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inverter Efficiency</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatNumber(data[0]?.inverterEfficiency || 0)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inverter Breakdown</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatNumber(data[0]?.inverterBreakdown || 0)} kWh
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Settings className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inverter Clipping</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {formatNumber(data[0]?.inverterClipping || 0)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Droplets className="h-5 w-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Soiling Loss</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {formatNumber(data[0]?.soilingLoss || 0)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div> : <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No inverter efficiency data available</p>
          </div>}
      </CardContent>
    </Card>;
};
export default InverterEfficiencyChart;